import { CallTypes } from "../../core/Enums"
import { useLiveCalls, usePhoneLines } from "../../core/Global"
import { CallStateSnapshot } from "../../core/SipPhoneManager"

export interface CallJobSource {
    jobSourceId?: string
    jobSourceName?: string
}

// Numbers reach us in three shapes at once: E.164 from the backend (+16064882783), the
// display format the softphone builds ((606) 488-2783) and whatever the PBX put in the DID.
// The last ten digits are the only part all three agree on.
const tail = (value?: string | null) => {
    const digits = (value ?? "").replace(/\D/g, "")
    return digits.length > 10 ? digits.slice(-10) : digits
}

/**
 * Resolves the job source behind a live softphone call.
 *
 * The dialed number is what identifies the line -- one extension answers for several numbers,
 * so the ringing extension says nothing about which one was called. It arrives in the X-DID
 * header the PBX seeds on the inbound call, and that is the path that carries no delay.
 *
 * When the header is missing (a PBX that has not had the dialplan hook installed, or a route
 * that bypasses it) we fall back to the live-call record the backend publishes over the socket:
 * it resolves the line from the Asterisk channel events rather than from SIP, so it knows the
 * source for a call the softphone cannot place. It lands a moment after the phone starts
 * ringing, which is why it is the fallback and not the primary -- the badge fills in late
 * instead of never, and Add Job, clicked seconds later, has it by then.
 */
export function useCallJobSource(call?: CallStateSnapshot): CallJobSource {

    const phoneLines = usePhoneLines()
    const liveCalls = useLiveCalls()

    if (!call) return {}

    const line = call.dialedNumber
        ? phoneLines.find(p => p.phoneNumber === call.dialedNumber)
        : undefined

    // A matched line is the answer even when it carries no source -- falling through would
    // only find the same line again through the live call.
    if (line) return { jobSourceId: line.jobSourceId, jobSourceName: line.jobSourceName }

    if (call.direction !== "incoming") return {}

    const caller = tail(call.callerId)
    if (!caller) return {}

    // Two simultaneous calls from one number would be ambiguous, so prefer the one that still
    // carries a source and, among those, the one that started most recently.
    const match = (liveCalls ?? [])
        .filter(c => c.callType === CallTypes.Incoming && c.jobSourceId && tail(c.phoneNumber) === caller)
        .sort((a, b) => (a.ringingSeconds ?? 0) - (b.ringingSeconds ?? 0))[0]

    if (!match) return {}

    return { jobSourceId: match.jobSourceId, jobSourceName: match.jobSource?.name }
}
